import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  ScrollText,
  User,
  Edit,
  Trash2,
  Plus,
  Eye,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { apiRequest } from '../lib/api';

interface AuditLogEntry {
  id: string;
  admin_user_id: string;
  action: string;
  resource_type: string;
  resource_id: string | null;
  old_value: any;
  new_value: any;
  created_at: string;
}

function getActionIcon(action: string) {
  switch (action) {
    case 'create':
      return <Plus className="w-3 h-3 text-emerald-400" />;
    case 'update':
      return <Edit className="w-3 h-3 text-amber-400" />;
    case 'delete':
      return <Trash2 className="w-3 h-3 text-red-400" />;
    default:
      return <Eye className="w-3 h-3 text-blue-400" />;
  }
}

function getActionColor(action: string) {
  switch (action) {
    case 'create':
      return 'bg-emerald-500/20 text-emerald-400';
    case 'update':
      return 'bg-amber-500/20 text-amber-400';
    case 'delete':
      return 'bg-red-500/20 text-red-400';
    default:
      return 'bg-blue-500/20 text-blue-400';
  }
}

function JsonDiff({ oldValue, newValue }: { oldValue: any; newValue: any }) {
  if (!oldValue && !newValue) return <span className="text-zinc-500">-</span>;

  const [expanded, setExpanded] = useState(false);

  const formatValue = (value: any) => {
    if (!value) return null;
    return JSON.stringify(value, null, 2);
  };

  const old = formatValue(oldValue);
  const next = formatValue(newValue);

  if (!old && !next) return <span className="text-zinc-500">-</span>;

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-zinc-400 hover:text-white"
      >
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {expanded ? 'Hide details' : 'Show details'}
      </button>

      {expanded && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          {old && (
            <div>
              <p className="text-xs text-zinc-500 mb-1">Previous</p>
              <pre className="text-xs bg-red-500/10 p-2 text-red-300 overflow-auto max-h-40 font-mono">
                {old}
              </pre>
            </div>
          )}
          {next && (
            <div>
              <p className="text-xs text-zinc-500 mb-1">New</p>
              <pre className="text-xs bg-emerald-500/10 p-2 text-emerald-300 overflow-auto max-h-40 font-mono">
                {next}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AuditLog() {
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState<string>('');
  const [resourceFilter, setResourceFilter] = useState<string>('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-audit-log', page, actionFilter, resourceFilter],
    queryFn: () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '30',
        ...(actionFilter && { action: actionFilter }),
        ...(resourceFilter && { resource_type: resourceFilter }),
      });
      return apiRequest('GET', `/api/admin/audit-log?${params}`);
    },
  });

  const resourceTypes = [
    'user',
    'user_role',
    'fee_tier',
    'user_fee_override',
    'payout',
    'referral',
    'referral_code',
    'job',
  ];

  const actions = ['create', 'update', 'delete', 'view', 'approve', 'reject', 'run'];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-medium text-white">Audit Log</h1>
          <p className="text-sm text-zinc-500">Track all admin actions and changes</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <select
          value={actionFilter}
          onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm bg-black border border-white/10 text-zinc-300"
        >
          <option value="">All Actions</option>
          {actions.map(action => (
            <option key={action} value={action}>{action}</option>
          ))}
        </select>
        <select
          value={resourceFilter}
          onChange={(e) => { setResourceFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 text-sm bg-black border border-white/10 text-zinc-300"
        >
          <option value="">All Resources</option>
          {resourceTypes.map(type => (
            <option key={type} value={type}>{type.replace('_', ' ')}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-600" />
        </div>
      ) : data?.logs?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 border border-white/10">
          <ScrollText className="w-8 h-8 text-zinc-600 mb-2" />
          <p className="text-sm text-zinc-500">No audit log entries found</p>
        </div>
      ) : (
        <>
          <div className="border border-white/10 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5">
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Timestamp</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Admin</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Action</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Resource</th>
                  <th className="text-left text-xs text-zinc-500 px-4 py-3">Changes</th>
                </tr>
              </thead>
              <tbody>
                {data?.logs?.map((entry: AuditLogEntry) => (
                  <tr key={entry.id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3">
                      <div className="text-xs text-zinc-300">
                        {new Date(entry.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {new Date(entry.created_at).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-white/5 flex items-center justify-center">
                          <User className="w-3 h-3 text-zinc-500" />
                        </div>
                        <span className="text-xs font-mono text-zinc-300">
                          {entry.admin_user_id.slice(0, 6)}...{entry.admin_user_id.slice(-4)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`flex items-center gap-1 text-xs px-2 py-0.5 w-fit ${getActionColor(entry.action)}`}>
                        {getActionIcon(entry.action)}
                        {entry.action}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-zinc-300">
                        {entry.resource_type.replace('_', ' ')}
                      </div>
                      {entry.resource_id && (
                        <div className="text-xs font-mono text-zinc-500">
                          {entry.resource_id.length > 12
                            ? `${entry.resource_id.slice(0, 6)}...${entry.resource_id.slice(-4)}`
                            : entry.resource_id
                          }
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <JsonDiff oldValue={entry.old_value} newValue={entry.new_value} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data?.pagination && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-xs text-zinc-500">
                Showing {data.logs.length} of {data.pagination.total} entries
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-zinc-400">
                  Page {page} of {data.pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= data.pagination.totalPages}
                  className="p-1.5 text-zinc-400 hover:text-white disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
